import { AxiosError, AxiosResponse } from 'axios';
import {
    GetDepositFilesResponse,
    TCompleteFile
} from 'lib-ui/vv-uploader/vv-types';
import { MutationOptions, useMutation, useQueryClient } from 'react-query';
import { getSpectreAuthController } from 'lib-ui/vv-uploader/use-token';

import useDepositNowStore from 'core/stores/deposit-now-store';

type ReactQueryError<TError> = {
    error: TError;
    file: Partial<TCompleteFile>;
    response: AxiosResponse;
};

function useDeleteFile(
    options?: Omit<
        MutationOptions<
            AxiosResponse<void, unknown>,
            ReactQueryError<AxiosError>,
            TCompleteFile
        >,
        'mutationFn'
    >
) {
    const { fileReference, spectreAgentUrl, vaultToken } = useDepositNowStore();
    const queryClient = useQueryClient();
    const spectreAuthController = getSpectreAuthController(vaultToken);

    return useMutation<
        AxiosResponse,
        ReactQueryError<AxiosError>,
        TCompleteFile
    >(
        (file) => {
            const depositUniqueReference =
                file.depositUniqueReference ??
                fileReference[file.fileName as string].dur;

            const uniqueReference =
                file.uniqueReference ??
                fileReference[file.fileName as string].ur;

            const baseURL = spectreAgentUrl;
            return spectreAuthController
                .request({
                    baseURL,
                    path: `/api/uploads/${depositUniqueReference}/${uniqueReference}`,
                    method: 'DELETE',
                    secure: true
                })
                .then((response) => {
                    queryClient.setQueryData(
                        ['use-agreements-deposits-files', file.depositId],
                        (
                            existingData: GetDepositFilesResponse = {
                                depositFiles: []
                            }
                        ) => {
                            return {
                                ...existingData,
                                depositFiles: existingData.depositFiles.filter(
                                    (depositFile) =>
                                        depositFile.fileId !== file.fileId
                                )
                            };
                        }
                    );
                    return response;
                })
                .catch((error) => {
                    const serverError: ReactQueryError<AxiosError> = {
                        error,
                        file,
                        response: error.response
                    };
                    throw serverError;
                });
        },
        {
            ...options,
            onError: ({ error, file, response }) => {
                if (error && error.response?.status === 404) {
                    queryClient.setQueryData(
                        ['use-agreements-deposits-files', file.depositId],
                        (
                            existingData: GetDepositFilesResponse = {
                                depositFiles: []
                            }
                        ) => {
                            return {
                                ...existingData,
                                depositFiles: existingData.depositFiles.filter(
                                    (depositFile) =>
                                        depositFile.fileId !== file.fileId
                                )
                            };
                        }
                    );
                }
            }
        }
    );
}

export default useDeleteFile;
